import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { useAuth } from '@/hooks/auth/use-auth';
import {
  NATIVE_SMOKE_ACTIVE_KEY,
  NATIVE_SMOKE_ROUTE_ACK_KEY,
  NATIVE_SMOKE_ROUTE_KEY,
  normalizeSmokeRoute,
} from '@/lib/native-app-routes';

export const NativeSmokeRouteListener = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useAuth();
  const lastHandledRouteRef = useRef<string | null>(null);
  const nativeSmokeRoutesEnabled =
    import.meta.env.DEV || import.meta.env.VITE_ENABLE_NATIVE_SMOKE === 'true';

  useEffect(() => {
    if (!nativeSmokeRoutesEnabled || !Capacitor.isNativePlatform()) {
      return;
    }

    const acknowledgeRoute = async () => {
      const mountedRoute = `${location.pathname}${location.search}${location.hash}`;
      window.localStorage.setItem(NATIVE_SMOKE_ROUTE_ACK_KEY, mountedRoute);
      await Preferences.set({ key: NATIVE_SMOKE_ROUTE_ACK_KEY, value: mountedRoute });
    };

    void acknowledgeRoute();
  }, [location.hash, location.pathname, location.search, nativeSmokeRoutesEnabled]);

  useEffect(() => {
    if (!nativeSmokeRoutesEnabled || !Capacitor.isNativePlatform()) {
      return;
    }

    if (loading) {
      return;
    }

    let cancelled = false;

    const syncSmokeRoute = async () => {
      const localRoute = window.localStorage.getItem(NATIVE_SMOKE_ROUTE_KEY);
      const { value: nativeRoute } = await Preferences.get({ key: NATIVE_SMOKE_ROUTE_KEY });
      const pendingRoute = localRoute ?? nativeRoute;

      if (!pendingRoute || cancelled) {
        return;
      }

      window.localStorage.setItem(NATIVE_SMOKE_ACTIVE_KEY, '1');

      const route = normalizeSmokeRoute(pendingRoute);
      if (!route) {
        window.localStorage.removeItem(NATIVE_SMOKE_ROUTE_KEY);
        await Preferences.remove({ key: NATIVE_SMOKE_ROUTE_KEY });
        return;
      }

      const currentRoute = `${location.pathname}${location.search}${location.hash}`;
      if (route === currentRoute || lastHandledRouteRef.current === route) {
        window.localStorage.removeItem(NATIVE_SMOKE_ROUTE_KEY);
        await Preferences.remove({ key: NATIVE_SMOKE_ROUTE_KEY });
        return;
      }

      lastHandledRouteRef.current = route;
      window.localStorage.removeItem(NATIVE_SMOKE_ROUTE_KEY);
      await Preferences.remove({ key: NATIVE_SMOKE_ROUTE_KEY });
      navigate(route, { replace: true });
    };

    void syncSmokeRoute();

    return () => {
      cancelled = true;
    };
  }, [loading, location.hash, location.pathname, location.search, nativeSmokeRoutesEnabled, navigate]);

  return null;
};
